import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X, Plus, Check } from 'lucide-react';
import PositionPicker from './PositionPicker';
import {
  BJJ_MASTERY_LEVELS,
  BJJ_COMMON_SUBMISSIONS,
  formatPosition,
  newStep,
} from '../../data/combatives';
import SelectWithOther from './SelectWithOther';

/**
 * TechniqueRow — a single technique entry in the BJJ session log.
 *
 * Collapsed (default): shows position summary + name + mastery pill on one line.
 * Expanded: 3-level position picker, name, per-row weapon toggle (reveals
 * weapon name + Offensive/Defensive pills), step editor with per-step
 * needsDrilling flag, freeform detail, reps, mastery picker, optional
 * submission.
 *
 * Props:
 *   value      — technique object (see newTechniqueRow())
 *   onChange   — (next) => void
 *   onRemove   — () => void
 *   index      — for test ids
 */
const TechniqueRow = ({ value, onChange, onRemove, index, nameSuggestions = [] }) => {
  const [expanded, setExpanded] = useState(true);
  const positionLabel = formatPosition(value.position);
  const summary = [positionLabel, value.name].filter(Boolean).join(' · ') || 'New technique';
  const isWeaponsTechnique = value.weapon !== null && value.weapon !== undefined;

  const set = (patch) => onChange({ ...value, ...patch });

  const toggleWeapons = (on) => {
    if (on) set({ weapon: '', weaponMode: 'Offensive' });
    else set({ weapon: null, weaponMode: null });
  };

  const updateStep = (idx, patch) => {
    const next = value.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    set({ steps: next });
  };
  const addStep = () => set({ steps: [...value.steps, newStep()] });
  const removeStep = (idx) => set({ steps: value.steps.filter((_, i) => i !== idx) });

  return (
    <div className="bg-enf-surface-mid border border-enf-surface-border rounded-lg overflow-hidden">
      {/* Collapsed header — always visible */}
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          data-testid={`technique-expand-${index}`}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          {expanded ? <ChevronDown className="w-4 h-4 text-enf-text-muted flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-enf-text-muted flex-shrink-0" />}
          <span className="text-sm font-semibold text-enf-text-primary truncate">{summary}</span>
        </button>
        {value.mastery && (
          <span className="px-1.5 py-0.5 rounded bg-enf-surface-deep border border-enf-surface-border font-mono text-[10px] text-enf-text-secondary flex-shrink-0">
            {value.mastery}
          </span>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove technique"
          data-testid={`technique-remove-${index}`}
          className="text-enf-text-muted hover:text-enf-role-warning p-1 -m-1 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-enf-surface-border pt-3">
          {/* Position */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Position</label>
            <PositionPicker
              value={value.position || { primary: '', secondary: '', tertiary: '' }}
              onChange={(pos) => set({ position: pos })}
              testId={`technique-${index}-position`}
            />
          </div>

          {/* Name — SelectWithOther wraps the user-history autocomplete
              array (nameSuggestions). For new users with empty history
              the component degrades to a plain text input automatically;
              experienced loggers get a select of their own historical
              names plus an "Other (specify)" escape hatch. Matches the
              Judo dropdown styling exactly (Adaptive Mode friendly). */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Technique name</label>
            <SelectWithOther
              value={value.name}
              onChange={(name) => set({ name })}
              suggestions={nameSuggestions}
              placeholder="Select or type a technique…"
              testId={`technique-${index}-name`}
            />
          </div>

          {/* Weapons — per-technique toggle. Weapons isn't a
              session-wide mode for BJJ; it's a per-technique attribute. The
              user can record one weapons drill alongside non-weapons drills
              in the same session without every row carrying empty weapon
              fields. */}
          <div className={`p-2.5 rounded-md border ${isWeaponsTechnique ? 'bg-enf-surface-deep border-enf-role-info/30' : 'bg-enf-surface-deep/50 border-enf-surface-border'}`}>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isWeaponsTechnique}
                onChange={(e) => toggleWeapons(e.target.checked)}
                data-testid={`technique-${index}-weapon-toggle`}
                className="w-4 h-4 rounded border-enf-surface-border bg-enf-surface-mid"
              />
              <span className="text-xs text-enf-text-secondary">This technique involves a weapon</span>
            </label>
            {isWeaponsTechnique && (
              <div className="mt-3 space-y-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Weapon</label>
                  <input
                    type="text"
                    value={value.weapon || ''}
                    onChange={(e) => set({ weapon: e.target.value })}
                    placeholder="e.g. Single stick, Knife, Karambit"
                    data-testid={`technique-${index}-weapon`}
                    className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Use</label>
                  <div className="flex gap-1.5">
                    {['Offensive', 'Defensive'].map((mode) => {
                      const active = value.weaponMode === mode;
                      return (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => set({ weaponMode: mode })}
                          data-testid={`technique-${index}-weaponmode-${mode.toLowerCase()}`}
                          aria-pressed={active}
                          className={`px-3 py-1.5 text-xs rounded-md border font-semibold transition-colors ${
                            active
                              ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                              : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                          }`}
                        >
                          {mode}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submission (optional) */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Submission (if applicable)</label>
            <SelectWithOther
              value={value.submission || ''}
              onChange={(submission) => set({ submission })}
              suggestions={BJJ_COMMON_SUBMISSIONS}
              placeholder="Select submission…"
              testId={`technique-${index}-submission`}
            />
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Step-by-step</label>
              <span className="text-[10px] text-enf-text-muted">Tap the flag to mark a step needing more drilling</span>
            </div>
            <div className="space-y-1.5">
              {value.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="font-mono text-xs text-enf-text-muted pt-2 w-5 flex-shrink-0">{i + 1}.</span>
                  <input
                    type="text"
                    value={step.text}
                    onChange={(e) => updateStep(i, { text: e.target.value })}
                    placeholder="What's the move at this step?"
                    data-testid={`technique-${index}-step-${i}-text`}
                    className="flex-1 min-w-0 px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                  />
                  <button
                    type="button"
                    onClick={() => updateStep(i, { needsDrilling: !step.needsDrilling })}
                    aria-pressed={step.needsDrilling}
                    title={step.needsDrilling ? 'Marked as needs more drilling' : 'Mark as needs more drilling'}
                    data-testid={`technique-${index}-step-${i}-flag`}
                    className={`px-2 py-1.5 rounded border text-[10px] font-semibold uppercase tracking-wider transition-colors flex-shrink-0 ${
                      step.needsDrilling
                        ? 'bg-enf-role-warning/15 border-enf-role-warning/40 text-enf-role-warning'
                        : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-muted hover:text-enf-role-warning hover:border-enf-role-warning/40'
                    }`}
                  >
                    {step.needsDrilling ? 'Drilling' : 'OK'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeStep(i)}
                    aria-label="Remove step"
                    data-testid={`technique-${index}-step-${i}-remove`}
                    className="p-1.5 text-enf-text-muted hover:text-enf-role-warning flex-shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addStep}
                data-testid={`technique-${index}-add-step`}
                className="w-full py-1.5 border border-dashed border-enf-surface-border hover:border-enf-role-info text-xs text-enf-text-muted hover:text-enf-role-info rounded-md transition-colors flex items-center justify-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add step
              </button>
            </div>
          </div>

          {/* Detail */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Extra notes (optional)</label>
            <textarea
              value={value.detail || ''}
              onChange={(e) => set({ detail: e.target.value })}
              rows={2}
              placeholder="Scenario context, key grips, common mistakes, anything else"
              data-testid={`technique-${index}-detail`}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none"
            />
          </div>

          {/* Reps + Mastery row */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Reps (optional)</label>
              <input
                type="text"
                inputMode="numeric"
                value={value.reps || ''}
                onChange={(e) => set({ reps: e.target.value })}
                placeholder="e.g. 20"
                data-testid={`technique-${index}-reps`}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Mastery</label>
              <select
                value={value.mastery || 'Drilling'}
                onChange={(e) => set({ mastery: e.target.value })}
                data-testid={`technique-${index}-mastery`}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
              >
                {BJJ_MASTERY_LEVELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[10px] text-enf-text-muted leading-snug">
            {BJJ_MASTERY_LEVELS.find((m) => m.value === value.mastery)?.help}
          </p>

          {/* Where did this take you? — optional, collapsed by default.
              Captures the transition edge for Game Maps: "Closed Guard
              (Bottom) → Mount (Top)" creates a directed edge in the
              user's My Map when Game Maps ships. Fully optional so the
              technique row doesn't get noisier for users who don't care
              about transition tracking. (G-Maps forward-compat) */}
          <NextPositionField value={value.nextPosition} onChange={(np) => set({ nextPosition: np })} index={index} />
        </div>
      )}
    </div>
  );
};

// NextPositionField — collapsed-by-default sub-form for the optional
// `nextPosition` capture. Stores `{ primary, secondary, tertiary, playerSide }`
// or null. Kept inline (not extracted) because it's only used here.
const NextPositionField = ({ value, onChange, index }) => {
  const [open, setOpen] = React.useState(!!value);
  const np = value || { primary: '', secondary: '', tertiary: '', playerSide: null };
  const summary = value
    ? formatPosition(np) + (np.playerSide ? ` (${np.playerSide})` : '')
    : 'Where did this take you? (optional)';

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); onChange({ primary: '', secondary: '', tertiary: '', playerSide: null }); }}
        data-testid={`technique-${index}-add-nextpos`}
        className="w-full px-3 py-2 border border-dashed border-enf-surface-border hover:border-enf-role-info rounded-md text-xs text-enf-text-muted hover:text-enf-role-info transition-colors flex items-center justify-center gap-1.5"
      >
        <Plus className="w-3 h-3" /> {summary}
      </button>
    );
  }

  return (
    <div className="p-2.5 bg-enf-surface-deep/50 border border-enf-surface-border rounded-md space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Where did this take you?</label>
        <button
          type="button"
          onClick={() => { setOpen(false); onChange(null); }}
          aria-label="Clear transition"
          data-testid={`technique-${index}-clear-nextpos`}
          className="text-enf-text-muted hover:text-enf-role-warning text-[10px]"
        >
          Clear
        </button>
      </div>
      <PositionPicker
        value={{ primary: np.primary, secondary: np.secondary, tertiary: np.tertiary }}
        onChange={(pos) => onChange({ ...np, primary: pos.primary, secondary: pos.secondary, tertiary: pos.tertiary })}
        testId={`technique-${index}-nextpos`}
      />
      <div className="flex gap-1.5">
        {['Top', 'Bottom', 'Neutral'].map((side) => {
          const active = np.playerSide === side;
          return (
            <button
              key={side}
              type="button"
              onClick={() => onChange({ ...np, playerSide: active ? null : side })}
              aria-pressed={active}
              data-testid={`technique-${index}-nextpos-side-${side.toLowerCase()}`}
              className={`flex-1 px-2 py-1 text-[11px] rounded border font-semibold transition-colors ${
                active
                  ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                  : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
              }`}
            >
              {side}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TechniqueRow;
